import axios from 'axios'
import LoginUserDef from '../login/LoginUserDef'
import NewFirstUserDef from './NewFirstUserDef'
const { server } = (window as any).config

export function checkSession() {
  axios.defaults.withCredentials = true
  return axios.get(server + '/session')
}

/**
 * Switch the session to another facility.
 *
 * The server re-derives the allowed list and rejects anything outside it, so the
 * token here is only ever a choice from a list the server produced. It also
 * re-narrows session.groups, which is what every patient query reads — so the
 * caller must refresh the session afterwards rather than patching state locally.
 */
export function setActiveFacility(facilityId: string) {
  axios.defaults.withCredentials = true
  return axios.post(server + '/me/facility', { facilityId })
}

/** The signed-in user's own editable details. */
export interface MyProfile {
  firstName: string
  lastName: string
  email: string
  role: string
  phoneNumber: string
  notificationsEnabled: boolean
  /** Doctors and nurses only; the server refuses phone/notification changes otherwise. */
  canManageNotifications: boolean
  pcc: { linked: boolean; externalId: string | null; externalUserName: string | null }
}

export type MyProfileUpdate = Partial<
  Pick<MyProfile, 'firstName' | 'lastName' | 'phoneNumber' | 'notificationsEnabled'>
>

/** Fired on window after the user saves their profile; the navbar re-reads the name. */
export const PROFILE_UPDATED_EVENT = 'mediora:profileUpdated'

export function getMyProfile() {
  axios.defaults.withCredentials = true
  return axios.get<MyProfile>(server + '/me/profile')
}

export function updateMyProfile(update: MyProfileUpdate) {
  axios.defaults.withCredentials = true
  return axios.post<MyProfile>(server + '/me/profile', update)
}

export function disconnectPcc() {
  axios.defaults.withCredentials = true
  return axios.post(server + '/me/pcc/disconnect')
}

/**
 * Connecting is a full-page OAuth redirect through PCC, not an XHR — the server
 * links the account on the way back and returns to /profile?pccLink=<status>.
 */
export function connectPcc() {
  window.location.href = `${server}/pcc/connect`
}

export function login(user: LoginUserDef) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/login', user)
}

export function createFirstUser(user: NewFirstUserDef) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/user_first_super_admin', user)
}

export function logOutUser() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + `/logout`)
}

export async function endShiftUser() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + `/endshift`)
}

export function setWebexCode(request: { code: string }) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/setWebexCode', request)
}

export function getWebexToken() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/getWebexToken')
}

export function getVideoToken(request: any) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/getVideoToken', request)
}

export function cancelVideoToken(request: any) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/cancelVideoToken', request)
}

export function refreshWebexToken() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/refreshWebexToken')
}

export function getBotToken() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/getBotToken')
}

export function removeAppoitmentDoctor(id: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/appointments/deleteDoctor/' + id)
}

export function getUserDetails(id: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/userDetails/' + id)
}

export function dashboard() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/dashboard')
}

export function dashboardAppointments(date: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + `/dashboard/appointments/${date}?currentDate=${new Date().toISOString()}`)
}

export function dashboardPatientByCategories(categories: any[]) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + `/dashboard/patientByCategories`, { categories: categories })
}

export function navbarDashboard() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + `/dashboard/navbar?currentDate=${new Date().toISOString()}`)
}

export function nurseDashboard() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.get(url + '/dashboard/nurseDashboard')
}

export function userActivationAction(userId: string, registrationGuid: string, language: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/userActivationAction', {
    userId: userId,
    registrationGuid: registrationGuid,
    language: language,
  })
}

export function userActivationResendAction(email: string, language: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/userActivationResendAction', {
    email: email,
    language: language,
  })
}

/**
 * Off-board a user. The account is kept - it is named on exams and orders - but
 * every way back into it is shut, live sessions are dropped and the contact
 * details are cleared. Reversible through usersEnable.
 */
export function usersDisable(id: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + `/users/disable/${id}`)
}

/**
 * Undo a disable. The account comes back at the status it had, so one that never
 * activated returns pending rather than signed in. Responds with
 * `contactDetailsMissing` when disable() cleared the email and nobody has put one
 * back yet.
 */
export function usersEnable(id: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + `/users/enable/${id}`)
}

export function usersResendRegistration(id: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + `/users/resendRegistration/${id}`)
}

export function userPasswordSetUpAction(
  userId: string,
  registrationGuid: string,
  password: string,
  passwordConfirmation: string,
  language: string,
) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userPasswordSetUpAction', {
    userId: userId,
    registrationGuid: registrationGuid,
    password: password,
    passwordConfirmation: passwordConfirmation,
    language: language,
  })
}

export function userPasswordForgotAction(userName: string, language: string, source?: string) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userPasswordForgotAction', {
    userName: userName,
    language: language,
    source: source,
  })
}

export function userPasswordResetAction(
  userId: string,
  forgotPasswordGuid: string,
  password: string,
  passwordConfirmation: string,
  language: string,
) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userPasswordResetAction', {
    userId: userId,
    forgotPasswordGuid: forgotPasswordGuid,
    password: password,
    passwordConfirmation: passwordConfirmation,
    language: language,
  })
}

export function userPasswordChangeForceAction(
  userName: string,
  changeForcePasswordGuid: string,
  password: string,
  passwordConfirmation: string,
  language: string,
) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userPasswordChangeForceAction', {
    userName: userName,
    changeForcePasswordGuid: changeForcePasswordGuid,
    password: password,
    passwordConfirmation: passwordConfirmation,
    language: language,
  })
}

export function loginMfa(user: LoginUserDef) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/login', user)
}

export function userPasswordResetVerify(userId: string, forgotPasswordGuid: string) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userPasswordResetVerify', {
    userId: userId,
    forgotPasswordGuid: forgotPasswordGuid,
  })
}

export function userUsernameForgotAction(email: string, language: string, source?: string) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userUsernameForgotAction', {
    email: email,
    language: language,
    source: source,
  })
}

export function userUsernameGetAction(emailEncrypt: string, forgotUsernameGuid: string) {
  const url = server
  axios.defaults.withCredentials = true

  return axios.post(url + '/userUsernameGetAction', {
    emailEncrypt: emailEncrypt,
    forgotUsernameGuid: forgotUsernameGuid,
  })
}

export function dashboardPatientsByMedicalStatus() {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + `/dashboard/dashboardPatientsByMedicalStatus`)
}

export function isPatientAuthorized(patientId: string) {
  const url = server
  axios.defaults.withCredentials = true
  return axios.post(url + '/patients/authorized', {
    patientId: patientId,
  })
}

export async function editEndShiftTime(endShiftTime: string) {
  const url = server
  axios.defaults.withCredentials = true
  return await axios.post(`${url}/users/editEndShiftTime`, { endShiftTime })
}

export async function getOwnedAppointemtns() {
  const url = server
  axios.defaults.withCredentials = true
  return await axios.get(`${url}/users/getOwnedAppointemtns`)
}

// The doctors each of `patientIds` may be treated by — i.e. the ones a nurse is
// allowed to act on behalf of. Scoped by shared group membership server-side, so
// a nurse covering several facilities gets each patient's own doctors.
export async function getDoctorsForPatients(patientIds: string[]) {
  const url = server
  axios.defaults.withCredentials = true
  return await axios.post(`${url}/getDoctorsForPatients`, { patientIds })
}
